import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { Button, Dropdown, theme } from 'antd';
import { LuDownload, LuFileDown, LuMap, LuTable } from 'react-icons/lu';
import { toLonLat } from 'ol/proj';

import { useVideoProject } from '@contexts/VideoProject.context';
import { useToast } from '@contexts/Toast.context';
import Loader from '@components/loading/Loader';
import { fetchUrl } from '@utils/api';
import { downloadFile } from '@utils/files';
import { startVideoProcessing } from '@utils/api/videoProjects';

import OverlayPlayer from './components/overlay/OverlayPlayer';
import CountPanel from './components/overlay/CountPanel';
import useCountModel from './components/overlay/useCountModel';
import FlightMap from './components/overlay/FlightMap';
import { animalPositions, indexFlight } from './components/overlay/flightModel';
import { fitStage } from './components/overlay/stageLayout';
import VideoPlayer from './components/results/VideoPlayer';
import KeyMetrics from './components/results/KeyMetrics';
import DetectionChart from './components/results/DetectionChart';
import VideoFlowShell from './flow/VideoFlowShell';
import StepRail from './flow/StepRail';
import JobProgressPanel, { jobPhase } from './flow/JobProgressPanel';
import { RateStrip, FilmStrip } from './flow/Strips';
import { ProcessedOverlay } from './flow/RangeOverlay';
import {
	ProjectCard,
	ProjectDetails,
	methodLabel,
	modelShortLabel,
	formatDuration,
} from './flow/ProjectCards';
import { useSignedUrl, useJsonFile, sourceVideoKey } from './flow/useResultFiles';
import { FlowPanel, PanelTitle, Muted, Switch, UNDER_PANEL_MIN_HEIGHT } from './flow/primitives';

/**
 * The video project page in the three-column flow (docs/video-flow-design/VIDEO_FLOW_DESIGN.html, screens 4-6):
 * analysing / waiting for a GPU / stopped while a job runs, and the results with the detections drawn on the video.
 * Jobs from before tracks.json existed keep the old player and metrics inside the same frame.
 */

const MODEL_FOOTER = 'BioDrone Foundation Model v2';

const csvOf = rows => rows.map(r => r.join(',')).join('\n');

// the video and, when the video carries flight data, the map of the flight beside it; a stage narrower than this
// shows the map as a minimap in the corner of the video instead
const SIDE_BY_SIDE_MIN_WIDTH = 1040;

/**
 * The results stage: video (and map) on top, the timeline strip under both. The rows and the video column come
 * from fitStage once the stage is measured, so the video is as large as the fixed-height page allows and the
 * strip takes the slack; until then the video is width-driven.
 */
const Stage = styled.div`
	flex: 1;
	min-height: 0;
	display: grid;
	grid-template-columns: ${({ $side }) =>
		$side ? 'minmax(0, 1.6fr) minmax(0, 1fr)' : 'minmax(0, 1fr)'};
	grid-template-rows: auto minmax(0, 1fr);
	grid-template-areas: ${({ $side }) =>
		$side ? `'video map' 'strip strip'` : `'video' 'strip'`};
	align-content: start;
	gap: ${({ theme }) => theme.token.marginSM}px;
`;

const Cell = styled.div`
	grid-area: ${({ $area }) => $area};
	min-width: 0;
	min-height: 0;
	display: flex;
	flex-direction: column;
	justify-self: ${({ $centre }) => ($centre ? 'center' : 'stretch')};
	max-width: 100%;
`;

const VideoProjectView = () => {
	const {
		project,
		projectUuid,
		isLoading,
		error,
		refetch,
		videoUuid,
		videoData,
		setSelectedJobUuid,
		resultsData: job,
		analysisData: analysis,
	} = useVideoProject();
	const { addToast } = useToast();
	const queryClient = useQueryClient();
	const playerRef = useRef(null);
	const [showDetections, setShowDetections] = useState(true);
	const [showLine, setShowLine] = useState(true);
	const [showMap, setShowMap] = useState(true);
	// on a narrow stage the map is a small view in the corner of the video; this swaps the two
	const [mapLarge, setMapLarge] = useState(false);
	const { token } = theme.useToken();
	const [stageEl, setStageEl] = useState(null);
	// the stage's box and the height of the player's control bar, which the video fit has to leave room for
	const [stageBox, setStageBox] = useState({ width: 0, height: 0, chrome: 0 });
	useEffect(() => {
		if (!stageEl) return undefined;
		const controls = playerRef.current?.controls?.();
		const measure = () => {
			const chrome = controls
				? Math.round(
						controls.getBoundingClientRect().height +
							parseFloat(getComputedStyle(controls).marginTop || 0)
					)
				: 0;
			const next = { width: stageEl.clientWidth, height: stageEl.clientHeight, chrome };
			setStageBox(prev =>
				prev.width === next.width &&
				prev.height === next.height &&
				prev.chrome === next.chrome
					? prev
					: next
			);
		};
		const ro = new ResizeObserver(measure);
		ro.observe(stageEl);
		if (controls) ro.observe(controls);
		return () => ro.disconnect();
	}, [stageEl]);
	const [retrying, setRetrying] = useState(false);

	const phase = jobPhase(job);
	// frames done so far while the animals are found and tracked (backend: video_detection._live_progress)
	const live = phase === 'running' && job?.live_progress?.frames_total ? job.live_progress : null;
	const paths = analysis?.result_paths || {};
	const tracksDoc = useJsonFile(phase === 'finished' ? paths.tracks : null);
	const cleanUrl = useSignedUrl(phase === 'finished' ? paths.clean_video : null);
	const legacy = phase === 'finished' && !(paths.tracks && paths.clean_video);
	const maskedUrl = useSignedUrl(legacy ? paths.masked_video : null);
	const timeSeries = useJsonFile(legacy ? paths.time_series : null);
	const sourceUrl = useSignedUrl(phase !== 'finished' ? sourceVideoKey(videoData) : null);
	const telemetryDoc = useJsonFile(phase === 'finished' ? paths.telemetry : null);
	const flight = useMemo(() => indexFlight(telemetryDoc), [telemetryDoc]);

	const m = useCountModel({
		tracksDoc,
		statistics: analysis?.statistics,
		virtualLine: analysis?.virtual_line,
	});
	const { setFrame } = m;
	const onFrame = useCallback(f => setFrame(f), [setFrame]);
	const seek = useCallback(sec => playerRef.current?.seekTo(sec), []);
	const fps = m.index?.fps;
	const seekFrame = useCallback(f => fps && seek((f + 0.5) / fps), [fps, seek]);

	const settings = useMemo(
		() => (job ? [methodLabel(job.counting_method), modelShortLabel(job)].filter(Boolean) : []),
		[job]
	);
	const analysedSeconds =
		job?.analysis_end_seconds != null
			? job.analysis_end_seconds - (job.analysis_start_seconds || 0)
			: videoData?.duration_seconds;
	const expectedMinutes = analysedSeconds
		? Math.max(2, Math.round((analysedSeconds * 5) / 60) + 2)
		: null;

	const exportPdf = useCallback(async () => {
		try {
			const data = await fetchUrl(`filelink?key=${paths.report_pdf}`);
			if (data?.url)
				downloadFile({ url: data.url, title: 'detection_report', fileExtension: 'pdf' });
		} catch (err) {
			console.error('Export failed:', err);
			addToast({
				id: 'export-pdf-error',
				bg: 'danger',
				title: 'Export failed',
				message: 'Could not download the detection report. Please try again.',
			});
		}
	}, [paths.report_pdf, addToast]);

	const animals = useMemo(() => animalPositions(m.index, flight), [m.index, flight]);

	const exportMap = useCallback(() => {
		if (!flight) return;
		const fps = m.index?.fps || flight.doc.fps || 30;
		const offset = flight.doc.time_offset_sec || 0;
		const lonLat = p => toLonLat(p).map(v => Number(v.toFixed(7)));
		const features = [
			{
				type: 'Feature',
				geometry: { type: 'LineString', coordinates: flight.pos.map(lonLat) },
				properties: { kind: 'flight_path' },
			},
			...animals.map(a => ({
				type: 'Feature',
				geometry: { type: 'Point', coordinates: lonLat(a.at) },
				properties: {
					kind: 'counted',
					track_id: m.index.tracks[a.track]?.id,
					counted_at_seconds: Number((offset + a.frame / fps).toFixed(2)),
					calf_sized: a.calf,
				},
			})),
		];
		const blob = new Blob([JSON.stringify({ type: 'FeatureCollection', features })], {
			type: 'application/geo+json',
		});
		const url = URL.createObjectURL(blob);
		downloadFile({ url, title: 'flight_map', fileExtension: 'geojson' });
		setTimeout(() => URL.revokeObjectURL(url), 10000);
	}, [flight, animals, m.index]);

	const exportCsv = useCallback(() => {
		if (!m.index || !m.series) return;
		const fps = m.index.fps;
		const offset = m.index.doc?.time_offset_sec || 0;
		const rows = [['time_seconds', 'count', 'in_view']];
		for (let f = 0; f < m.series.length; f += Math.max(1, Math.round(fps)))
			rows.push([(offset + f / fps).toFixed(2), m.series[f], m.index.inView[f]]);
		const url = URL.createObjectURL(new Blob([csvOf(rows)], { type: 'text/csv' }));
		downloadFile({ url, title: `counts_${m.mode}`, fileExtension: 'csv' });
		setTimeout(() => URL.revokeObjectURL(url), 10000);
	}, [m.index, m.series, m.mode]);

	// Re-run with the stored settings. A line job also needs its line, which the job record does not return yet.
	const canRetry = phase === 'stopped' && job && job.counting_method !== 'line_based';
	const retry = useCallback(async () => {
		setRetrying(true);
		try {
			const configuration = Object.fromEntries(
				Object.entries({
					counting_scenario: job.counting_scenario,
					counting_method: job.counting_method,
					text_prompt: job.text_prompt,
					model_id: job.model_id,
					model_version: job.model_version,
					target_fps: job.target_fps,
					analysis_start_seconds: job.analysis_start_seconds,
					analysis_end_seconds: job.analysis_end_seconds,
				}).filter(([, v]) => v != null)
			);
			const res = await startVideoProcessing({ projectUuid, videoUuid, configuration });
			setSelectedJobUuid(res?.data?.job_uuid || null);
			await refetch();
			queryClient.invalidateQueries({ queryKey: ['videoResults', projectUuid] });
		} catch (err) {
			addToast({
				id: 'video-retry-error',
				bg: 'danger',
				title: 'Could not start again',
				message: err?.message || 'Please try again in a moment.',
			});
		} finally {
			setRetrying(false);
		}
	}, [job, projectUuid, videoUuid, setSelectedJobUuid, refetch, queryClient, addToast]);

	const crumbs = [
		{ title: 'Projects', link: '/projects' },
		{ title: 'Video counting' },
		{ title: project?.title || 'Video project' },
	];

	if (isLoading || error || !project)
		return (
			<VideoFlowShell
				crumbs={crumbs}
				centre={
					isLoading ? (
						<Loader />
					) : (
						<FlowPanel>
							<PanelTitle>Could not load the project</PanelTitle>
							<Muted>{error?.message || 'This video project was not found.'}</Muted>
						</FlowPanel>
					)
				}
			/>
		);

	const finishedOverlay = phase === 'finished' && m.index && cleanUrl;
	const projectCard = (
		<ProjectCard project={project} video={videoData} analysis={analysis || job} />
	);

	let left;
	let centre;
	let right;
	if (phase === 'finished') {
		if (finishedOverlay) {
			const line = m.mode === 'line' && showLine ? m.line : null;
			const mapOn = !!flight && showMap;
			const sideBySide = mapOn && stageBox.width >= SIDE_BY_SIDE_MIN_WIDTH;
			const pipMap = mapOn && !sideBySide;
			const swapped = pipMap && mapLarge;
			const fit = fitStage({
				width: stageBox.width,
				height: stageBox.height,
				ratio: m.index.width / m.index.height,
				gap: token.marginSM,
				chrome: stageBox.chrome,
				side: sideBySide,
				stripFloor: UNDER_PANEL_MIN_HEIGHT,
			});
			const flightMap = mapOn && (
				<FlightMap
					flight={flight}
					index={m.index}
					frame={m.frame}
					projectUuid={projectUuid}
					onSeekFrame={seekFrame}
					animals={animals}
					compact={pipMap && !swapped}
				/>
			);
			const exportItems = [
				paths.report_pdf && {
					key: 'pdf',
					icon: <LuFileDown />,
					label: 'PDF report',
					onClick: exportPdf,
				},
				{ key: 'csv', icon: <LuTable />, label: 'CSV of counts', onClick: exportCsv },
				flight && {
					key: 'map',
					icon: <LuMap />,
					label: 'Map (GeoJSON)',
					onClick: exportMap,
				},
			].filter(Boolean);
			// no left column: the video, the map and the count get the width; the facts sit behind Details
			centre = (
				<>
					<Stage
						$side={sideBySide}
						ref={setStageEl}
						style={
							fit && {
								gridTemplateRows: `${fit.rowHeight}px ${fit.stripHeight}px`,
								gridTemplateColumns: sideBySide
									? `${fit.videoWidth}px minmax(0, 1fr)`
									: undefined,
							}
						}>
						<Cell
							$area="video"
							$centre={!sideBySide}
							style={fit ? { width: fit.videoWidth } : undefined}>
							<OverlayPlayer
								ref={playerRef}
								src={cleanUrl}
								index={m.index}
								line={line}
								lineEditable={!!line}
								onLineChange={m.setLine}
								onFrame={onFrame}
								showDetections={showDetections}
								aspect={`${m.index.width} / ${m.index.height}`}
								pip={pipMap ? flightMap : null}
								swapped={swapped}
								onSwap={() => setMapLarge(v => !v)}
								extraControls={
									<>
										<Switch
											label="Detections"
											on={showDetections}
											onChange={setShowDetections}
										/>
										{m.mode === 'line' && (
											<Switch
												label="Line"
												on={showLine}
												onChange={setShowLine}
											/>
										)}
										{flight && (
											<Switch
												label="Map"
												on={showMap}
												onChange={setShowMap}
											/>
										)}
										<ProjectDetails
											project={project}
											video={videoData}
											analysis={analysis || job}
										/>
										<Dropdown menu={{ items: exportItems }} trigger={['click']}>
											<Button size="small" icon={<LuDownload />}>
												Export
											</Button>
										</Dropdown>
									</>
								}
							/>
						</Cell>
						{sideBySide && <Cell $area="map">{flightMap}</Cell>}
						<Cell $area="strip">
							<RateStrip
								series={m.series}
								fps={m.index.fps}
								frame={m.frame}
								onSeek={seek}
								cumulative={m.mode !== 'max'}
								title={
									m.mode === 'max' ? 'Most animals in view' : 'Animals counted'
								}
							/>
						</Cell>
					</Stage>
				</>
			);
			right = (
				<CountPanel
					modes={['hover', 'line', 'max']}
					mode={m.mode}
					onModeChange={m.setMode}
					count={m.count}
					subtitle={m.subtitle}
					note={m.note}
					noteWarn={m.noteWarn}
					inView={m.index.inView[m.frame]}
					maxInView={m.index.maxInView}
					hist={m.hist}
					series={m.series}
					frame={m.frame}
					modelLabel={[MODEL_FOOTER, modelShortLabel(job)].filter(Boolean).join(' · ')}
					sensitivity={m.sensitivity}
				/>
			);
		} else if (legacy) {
			// the old player has no control bar to hold the details, so the card keeps its column
			left = projectCard;
			const stats = analysis?.statistics;
			centre = (
				<>
					<VideoPlayer
						ref={playerRef}
						videoUrl={maskedUrl}
						detections={job?.detections || []}
						virtualLine={analysis?.virtual_line}
					/>
					<DetectionChart
						data={timeSeries}
						onPointClick={p => seek(p.timestamp_sec)}
						countingMethod={stats?.counting_method_used}
					/>
				</>
			);
			right = (
				<FlowPanel $fill>
					<PanelTitle>Count</PanelTitle>
					<KeyMetrics
						totalCount={stats?.total_count}
						uniqueCount={stats?.unique_count}
						lineCrossNet={stats?.line_cross_net}
						average={stats?.average_count_per_frame}
						maxSimultaneousCount={stats?.max_simultaneous_count}
						countingMethod={stats?.counting_method_used}
						chartData={timeSeries}
						currentTime={0}
					/>
				</FlowPanel>
			);
		} else {
			centre = <Loader />;
			right = (
				<FlowPanel $fill>
					<PanelTitle>Loading the count…</PanelTitle>
				</FlowPanel>
			);
		}
	} else {
		const stepState =
			phase === 'waiting' ? 'waiting' : phase === 'stopped' ? 'stopped' : 'current';
		left = (
			<>
				<StepRail
					title="Steps"
					current={3}
					currentState={stepState}
					summaries={{
						0: videoData?.video_filename,
						1:
							job &&
							[
								methodLabel(job.counting_method),
								job.analysis_end_seconds != null &&
									`${formatDuration(job.analysis_start_seconds || 0)}–${formatDuration(job.analysis_end_seconds)}`,
							]
								.filter(Boolean)
								.join(' · '),
						3:
							phase === 'waiting'
								? 'Waiting for a GPU machine'
								: phase === 'stopped'
									? 'Stopped'
									: job
										? `${Math.max(0, job.progress || 0)} %`
										: 'Not started',
					}}
				/>
				{projectCard}
			</>
		);
		centre = (
			<>
				<OverlayPlayer ref={playerRef} src={sourceUrl} index={null} />
				<FilmStrip
					src={sourceUrl}
					title={live ? 'Analysed so far' : 'Preview'}
					right={
						live
							? `${live.frames_done} of ${live.frames_total} frames · ${live.counted_so_far} counted so far`
							: videoData?.duration_seconds
								? `0:00 – ${formatDuration(videoData.duration_seconds)}`
								: null
					}>
					{live && (
						<ProcessedOverlay
							duration={videoData?.duration_seconds}
							start={job?.analysis_start_seconds || 0}
							end={job?.analysis_end_seconds}
							frac={live.frames_total ? live.frames_done / live.frames_total : 0}
						/>
					)}
				</FilmStrip>
			</>
		);
		right = job ? (
			<JobProgressPanel
				job={job}
				settings={settings}
				expectedMinutes={expectedMinutes}
				onRetry={canRetry ? retry : null}
				retrying={retrying}
			/>
		) : (
			<FlowPanel $fill>
				<PanelTitle>No analysis yet</PanelTitle>
				<Muted>This video has not been analysed.</Muted>
			</FlowPanel>
		);
	}

	return <VideoFlowShell crumbs={crumbs} left={left} centre={centre} right={right} />;
};

export default VideoProjectView;
